import { MutationObserver, QueryClient } from '@tanstack/react-query';

// Preserve explicit request behavior during migration: no automatic replay of
// payments, validation, maintenance checks, or the existing top-up workflow.
export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
            staleTime: 0,
            gcTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            networkMode: 'always',
            structuralSharing: false,
        },
        mutations: { retry: false, networkMode: 'always', gcTime: 0 },
    },
});

let readContext: () => unknown = () => null;
let previousIdentity: string | undefined;
let previousAuth: string | undefined;
let previousContext: unknown = null;
let generation = 0;
const listeners = new Set<() => void>();

export function configureRequestContext(reader: () => unknown) {
    readContext = reader;
    syncRequestContext();
}

// Learning more of the same session (the login response knows only the user,
// the profile request then adds business/site) is not a new identity. Only a
// known value changing to another one is, e.g. switching business or site.
const refinesContext = (prev: any, next: any) => prev === null
    || (next !== null && Object.keys(prev).every(key => prev[key] === null || prev[key] === next[key]));

// Secrets are compared privately and never included in a query key.
function identity() {
    let auth: any = null;
    try { auth = JSON.parse(sessionStorage.getItem('authUser') || 'null'); } catch { /* Public pages. */ }
    const authIdentity = JSON.stringify([auth?.token ?? null, auth?.user?.id ?? null]);
    const context = readContext();
    return { authIdentity, context, key: JSON.stringify([authIdentity, context]) };
}

export function syncRequestContext() {
    const next = identity();
    if (next.key !== previousIdentity) {
        const initialized = previousIdentity !== undefined;
        // AuthProtected loads profile and menu concurrently. Learning the profile
        // must not cancel the valid menu request from the same session.
        const initialProfile = previousAuth === next.authIdentity
            && refinesContext(previousContext, next.context);
        previousIdentity = next.key;
        previousAuth = next.authIdentity;
        previousContext = next.context;
        if (initialized && !initialProfile) resetRequestCache();
    }
    return generation;
}

export function resetRequestCache() {
    generation += 1;
    // clear() cancels pending queries as well as removing cached private data.
    // Mutations are never replayed or rolled back on an identity change.
    queryClient.clear();
    listeners.forEach(listener => listener());
}

export const getRequestScope = () => syncRequestContext();
export const getRequestScopeSnapshot = () => generation;
export const subscribeRequestScope = (listener: () => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};

export async function executeMutation<T>(key: readonly unknown[], operation: () => Promise<T>): Promise<T> {
    const observer = new MutationObserver<T, unknown, void>(queryClient, {
        mutationKey: key,
        mutationFn: operation,
        retry: false,
        gcTime: 0,
    });
    try {
        return await observer.mutate();
    } finally {
        observer.reset();
    }
}
